import { motion } from "framer-motion";
import { fadeInUp, staggerContainer } from "@/motion/variants";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { FeatureBadges } from "@/components/MicroTrustBadges";
import { Button } from "@/components/ui/button";
import { ConsultingPartnersTrustBar } from "@/components/TrustBar";
import { Sparkles, Database, Cloud, Users, Cpu, Shield, BarChart3, GraduationCap, Zap, Target } from "lucide-react";

const Consulting = () => {
  return (
    <div className="min-h-screen pt-6">
      <SEO
        title="AI-First Business Consulting | Workflow Automation | Cloud Optimization | Padmora"
        description="Accelerate your business with AI-first consulting services. Workflow automation, data infrastructure optimization, and cloud platform enhancement for maximum efficiency and growth."
        image="/og-consulting.jpg"
        canonical="https://padmora.com/consulting"
        keywords={[
          "AI consulting",
          "business automation",
          "workflow optimization",
          "cloud platform consulting",
          "data infrastructure",
          "digital transformation",
          "process automation",
          "AI implementation",
          "business efficiency",
          "technology consulting",
          "cloud optimization",
          "automation consulting"
        ]}
        aiOptimization={{
          aiKeywords: [
            "artificial intelligence business consulting",
            "machine learning workflow automation",
            "AI digital transformation",
            "intelligent process optimization",
            "automated business intelligence"
          ],
          entities: [
            "Artificial Intelligence",
            "Workflow Automation",
            "Cloud Computing",
            "Data Infrastructure",
            "Digital Transformation",
            "Business Consulting"
          ],
          topics: [
            "AI Consulting",
            "Business Automation",
            "Digital Transformation",
            "Cloud Optimization",
            "Process Improvement"
          ]
        }}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "AI-First Business Consulting",
          "description": "Comprehensive AI-first consulting services for business acceleration and digital transformation.",
          "provider": {
            "@type": "Organization",
            "name": "Padmora",
            "url": "https://padmora.com"
          },
          "serviceType": "Business Consulting"
        }}
        breadcrumbs={[
          { name: "Home", url: "https://padmora.com/" },
          { name: "Consulting", url: "https://padmora.com/consulting" }
        ]}
      />
      <Navigation />

      {/* Hero Section */}
      <section className="min-h-[60vh] flex items-center justify-center">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto text-center"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-6xl font-bold text-slate-900 mb-6"
            >
              AI-first consulting to accelerate your business
            </motion.h1>
            <motion.div variants={fadeInUp}>
              <Button 
                size="lg" 
                className="bg-primary hover:bg-primary/90 text-white px-8 py-4"
                asChild
              >
                <a
                  href="mailto:michel@padmora.com"
                >
                  Request a proposal
                </a>
              </Button>
            </motion.div>

            {/* Previous Collaborations */}
            <motion.div variants={fadeInUp} className="mt-12">
              <ConsultingPartnersTrustBar />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Offer tiles */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            <motion.div
              variants={fadeInUp}
              className="bg-card/50 border border-border/50 p-6 rounded-lg hover:shadow-md transition-shadow"
            >
              <h3 className="text-xl font-bold mb-3">AI workflows & agents</h3>
              <p className="text-slate-600 mb-4">Operations, sales, procurement automation</p>
              <FeatureBadges
                features={[
                  { icon: <Sparkles className="h-4 w-4" />, label: "Custom AI agents" },
                  { icon: <Cpu className="h-4 w-4" />, label: "Workflow automation" },
                  { icon: <BarChart3 className="h-4 w-4" />, label: "Process optimization" }
                ]}
                align="left"
                size="sm"
                className="mt-4"
              />
            </motion.div>
            <motion.div
              variants={fadeInUp}
              className="bg-card/50 border border-border/50 p-6 rounded-lg hover:shadow-md transition-shadow"
            >
              <h3 className="text-xl font-bold mb-3">Data infrastructure</h3>
              <p className="text-slate-600 mb-4">ETL/ELT, lakehouse, BI</p>
              <FeatureBadges
                features={[
                  { icon: <Database className="h-4 w-4" />, label: "Data pipelines" },
                  { icon: <Cloud className="h-4 w-4" />, label: "Lakehouse architecture" },
                  { icon: <BarChart3 className="h-4 w-4" />, label: "BI dashboards" }
                ]}
                align="left"
                size="sm"
                className="mt-4"
              />
            </motion.div>
            <motion.div
              variants={fadeInUp}
              className="bg-card/50 border border-border/50 p-6 rounded-lg hover:shadow-md transition-shadow"
            >
              <h3 className="text-xl font-bold mb-3">Cloud platform</h3>
              <p className="text-slate-600 mb-4">Cost, reliability, security</p>
              <FeatureBadges
                features={[
                  { icon: <Cloud className="h-4 w-4" />, label: "Cloud optimization" },
                  { icon: <Shield className="h-4 w-4" />, label: "Security hardening" },
                  { icon: <Zap className="h-4 w-4" />, label: "Performance tuning" }
                ]}
                align="left"
                size="sm"
                className="mt-4"
              />
            </motion.div>
            <motion.div
              variants={fadeInUp}
              className="bg-card/50 border border-border/50 p-6 rounded-lg hover:shadow-md transition-shadow"
            >
              <h3 className="text-xl font-bold mb-3">Training & advisory</h3>
              <p className="text-slate-600 mb-4">Expert guidance and mentorship</p>
              <FeatureBadges
                features={[
                  { icon: <GraduationCap className="h-4 w-4" />, label: "Team training" },
                  { icon: <Users className="h-4 w-4" />, label: "Architecture review" },
                  { icon: <Target className="h-4 w-4" />, label: "Strategic planning" }
                ]}
                align="left"
                size="sm"
                className="mt-4"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Consulting;
